import { useAuthStore } from '../stores/auth';
import { api } from './client';

// Origo Clips - client-side API surface.
//
// Server-side handlers live in services/gateway/internal/clienthandler/clips.go.
// See docs/api/clips.md for the full schema.

export type ClipChain = {
  clip_id: string;
  event_id: string;
  event_type: string;
  person_id: string | null;
  person_name: string | null;
  team_id: string | null;
  team_name: string | null;
  rating: number | null;
  minute: number | null;
  second: number | null;
};

export type Clip = {
  id: string;
  event_id?: string;
  match_id: string;
  person_id: string | null;
  person_name: string | null;
  team_id: string | null;
  team_name: string | null;
  event_type: string;
  /** Later event in a desk chain - same video, shown as a sub-event. */
  chain?: ClipChain | null;
  video_start_ms: number;
  video_end_ms: number;
  duration_ms: number;
  rating: number | null;
  tags: string[];
  thumbnail_s3_key: string | null;
  occurred_at: string;
  minute: number | null;
  second: number | null;
  period: string | null;
  competition_code?: string;
  season_name?: string;
  // preview_url is the standalone MP4 written on Save. Playback must use
  // this file when present - a dead live stream must not affect the clip.
  preview_url?: string | null;
  preview_thumb_url?: string | null;
  preview_generated_at?: string | null;
  preview_generating?: boolean;
  preview_stale?: boolean;
  preview_error?: string | null;
  updated_at?: string;
  // Last-resort DVR playlist only when preview_url is missing. Never the
  // live broadcaster URL.
  hls_stream_url?: string;
};

export type ClipDetail = Clip;

export type ClipsFilter = {
  match_id?: string;
  person_id?: string;
  team_id?: string;
  min_rating?: number;
  event_types?: string[];
  tag?: string;
  limit?: number;
};

function encode(f: ClipsFilter): string {
  const p = new URLSearchParams();
  if (f.match_id) p.set('match_id', f.match_id);
  if (f.person_id) p.set('person_id', f.person_id);
  if (f.team_id) p.set('team_id', f.team_id);
  if (typeof f.min_rating === 'number') p.set('min_rating', String(f.min_rating));
  if (f.event_types?.length) p.set('event_types', f.event_types.join(','));
  if (f.tag) p.set('tag', f.tag);
  if (typeof f.limit === 'number') p.set('limit', String(f.limit));
  return p.toString();
}

export const listClips = (f: ClipsFilter = {}) => api.get<Clip[]>(`/client/v1/clips?${encode(f)}`);
export const getClip = (id: string) => api.get<ClipDetail>(`/client/v1/clips/${id}`);

// ---------------------------------------------------------------------------
// Highlights - the composer's back-end.
// ---------------------------------------------------------------------------

export type Highlight = {
  id: string;
  name: string;
  description: string | null;
  clip_ids: string[];
  watermark: boolean;
  total_duration_ms: number;
  render_status: 'draft' | 'queued' | 'running' | 'ready' | 'failed';
  render_error: string | null;
  output_s3_key: string | null;
  output_size_bytes: number | null;
  output_ready_at: string | null;
  created_at: string;
  updated_at: string;
  download_url?: string | null;
  preview_url?: string | null;
  poster_url?: string | null;
  /** thumbnail_url is set when a Brand Studio thumb template rendered a JPG
   *  for this reel. Otherwise the client should fall back to poster_url
   *  (which comes from the first clip). */
  thumbnail_url?: string | null;
  folder_id?: string | null;
  aspect?: '16:9' | '9:16';
  review_status?: 'published' | 'pending_review';
  sibling_reel_id?: string | null;
  match_id?: string | null;
  render_started_at?: string | null;
  render_finished_at?: string | null;
  render_progress?: number;
  render_stage?: string;
};

export type HighlightFolder = {
  id: string;
  name: string;
  video_count: number;
  created_at: string;
};

export const listHighlights = () => api.get<Highlight[]>('/client/v1/highlights');
export type ClipTransition = 'fade' | 'fadeblack' | 'wipeleft' | 'slideleft' | 'cut';

export const CLIP_TRANSITIONS: { value: ClipTransition; label: string; hint: string }[] = [
  { value: 'fade', label: 'Cross dissolve', hint: 'Soft blend between clips' },
  { value: 'fadeblack', label: 'Fade through black', hint: 'Dip to black, then in' },
  { value: 'wipeleft', label: 'Wipe left', hint: 'New clip wipes in from the right' },
  { value: 'slideleft', label: 'Slide left', hint: 'Outgoing clip slides off left' },
  { value: 'cut', label: 'Hard cut', hint: 'No transition - instant cut' },
];

export const createHighlight = (input: {
  name: string;
  description?: string | null;
  clip_ids: string[];
  watermark?: boolean;
  transition?: ClipTransition;
}) => api.post<{
  id: string;
  render_status: string;
  total_duration_ms: number;
  download_url?: string;
  preview_url?: string;
  poster_url?: string;
}>(
  '/client/v1/highlights', input);
export const deleteHighlight = (id: string) => api.delete<void>(`/client/v1/highlights/${id}`);
export const patchHighlight = (id: string, input: { folder_id?: string | null; name?: string }) =>
  api.patch<{ ok: boolean }>(`/client/v1/highlights/${id}`, input);

export const publishHighlight = (id: string) =>
  api.post<{ ok: boolean; review_status: string }>(`/client/v1/highlights/${id}/publish`, {});

export const listHighlightFolders = () => api.get<HighlightFolder[]>('/client/v1/highlight-folders');
export const createHighlightFolder = (name: string) =>
  api.post<HighlightFolder>('/client/v1/highlight-folders', { name });
export const renameHighlightFolder = (id: string, name: string) =>
  api.patch<{ id: string; name: string }>(`/client/v1/highlight-folders/${id}`, { name });
export const deleteHighlightFolder = (id: string) =>
  api.delete<void>(`/client/v1/highlight-folders/${id}`);

// ---------------------------------------------------------------------------
// Clip Rules - manual trigger + run history.
// ---------------------------------------------------------------------------

export type ClipRuleRun = {
  id: string;
  rule_id: string;
  rule_name?: string;
  match_id: string;
  trigger: 'auto' | 'manual' | 'dry_run';
  status: 'ok' | 'skipped' | 'failed';
  reason: string;
  reel_id: string | null;
  selected_clip_ids: string[];
  total_duration_ms: number;
  created_at: string;
};

export type ClipRuleTemplate = 'game_highlights' | 'goals_only' | 'player_reel' | 'condensed_game';
export type ClipRuleAspect = '16:9' | '9:16' | 'both';
export type ClipRulePublishMode = 'auto' | 'review';
export type ClipRuleTeam = 'both' | 'home' | 'away' | 'entitled';
export type ClipRuleOrder = 'chronological' | 'rating';

export type ClientClipRuleConfig = {
  aspect: ClipRuleAspect;
  publish_mode: ClipRulePublishMode;
  team: ClipRuleTeam;
  team_id?: string | null;
  order: ClipRuleOrder;
  periods?: string[];
  exclude_event_types?: string[];
  max_duration_sec: number;
  max_clip_duration_sec: number;
  max_per_team: number;
  watermark: boolean;
  transition: ClipTransition;
  title_template?: string;
};

export type ClientClipRule = {
  id: string;
  template: ClipRuleTemplate;
  name: string;
  sport_code: string;
  is_default: boolean;
  client_owned: boolean;
  competition_id: string | null;
  resolved: boolean;
  config: ClientClipRuleConfig;
};

export type RunClipRuleBody = {
  match_id: string;
  rule_id?: string;
  folder_id?: string | null;
  title?: string;
  transition?: ClipTransition;
  person_id?: string;
  aspect?: ClipRuleAspect;
  publish_mode?: ClipRulePublishMode;
  team?: ClipRuleTeam;
  order?: ClipRuleOrder;
  periods?: string[];
  exclude_event_types?: string[];
  max_duration_sec?: number;
  max_clip_duration_sec?: number;
  max_per_team?: number;
  watermark?: boolean;
};

export type RunClipRuleResp = {
  rule_id: string;
  status: 'ok' | 'skipped' | 'failed';
  reason: string;
  run_id: string;
  title: string;
  duration_ms: number;
  reel_id?: string;
  vertical_reel_id?: string;
  render_status?: string;
  review_status?: string;
};

export type PreviewClipRuleResp = {
  rule_id: string;
  rule_name: string;
  status: 'ok' | 'skipped' | 'failed';
  reason: string;
  title: string;
  total_duration_ms: number;
  selected_count: number;
  aspect?: ClipRuleAspect;
  publish_mode?: ClipRulePublishMode;
  selected: {
    clip_id: string;
    event_type: string;
    rating: number | null;
    duration_ms: number;
    occurred_at: string;
    selected_by: string;
    person_name: string;
    team_name: string;
    period: string;
  }[];
};

/**
 * Fires a clip-rule for the given match. The response's `status` field
 * distinguishes success (`ok`) from a guard-blocked run (`skipped`) - a
 * 200 does not mean a reel was actually created.
 */
export const runClipRule = (template: string, body: RunClipRuleBody) =>
  api.post<RunClipRuleResp>(`/client/v1/clip-rules/${template}/run`, body);

export const previewClipRule = (template: string, body: RunClipRuleBody) =>
  api.post<PreviewClipRuleResp>(`/client/v1/clip-rules/${template}/preview`, body);

export const listClientClipRules = (params?: { template?: string; match_id?: string }) => {
  const q = new URLSearchParams();
  if (params?.template) q.set('template', params.template);
  if (params?.match_id) q.set('match_id', params.match_id);
  const suffix = q.toString();
  return api.get<ClientClipRule[]>(`/client/v1/clip-rules${suffix ? `?${suffix}` : ''}`);
};

export const listClipRuleRuns = (params?: {
  match_id?: string;
  status?: 'ok' | 'skipped' | 'failed';
}) => {
  const q = new URLSearchParams();
  if (params?.match_id) q.set('match_id', params.match_id);
  if (params?.status) q.set('status', params.status);
  const suffix = q.toString();
  return api.get<ClipRuleRun[]>(`/client/v1/clip-rule-runs${suffix ? `?${suffix}` : ''}`);
};

export async function downloadHighlightFile(id: string, filename: string) {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`/client/v1/highlights/${id}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error('Download is not ready');
  }
  const type = res.headers.get('content-type') ?? '';
  if (type.includes('application/json')) {
    const data = await res.json() as { url?: string };
    if (data.url) {
      window.open(data.url, '_blank');
      return;
    }
    throw new Error('File is not ready yet');
  }
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename.endsWith('.mp4') ? filename : `${filename}.mp4`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4_000);
}
